import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import store from '@/store'
import { $t, getLanguage } from '@/lang/index'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { debounce } from 'lodash-es'

dayjs.extend(utc)
dayjs.extend(timezone)

interface AxiosResponseConfig extends AxiosResponse {
  config: AxiosRequestConfig
}

interface AxiosRequest extends AxiosRequestConfig {
  [key: string]: any
}

// 定义 ResultVo 类型（与后端结构一致）
interface ResultVo<T> {
  code: number
  status: number // 1: 成功, 0: 失败, 无字段：默认失败
  message: string
  data: T
  params?: any[]
  msg?: string
}

const state: any = store.state
const env = state.env.env
const request = axios.create({
  baseURL: '',
  timeout: 20000,
  headers: {
    zoneId: dayjs.tz.guess()
  }
})

function refreshToken() {
  // return axios.create()({
  //   method: 'GET',
  //   url: `${
  //     process.env.NODE_ENV === 'development' ? '' : 'https://authentication.yunexpress.com'
  //   }/Authentication/GetAuthentication`,
  //   params: {
  //     key: state.user.userInfo.token
  //   }
  // })
  const baseUrl = state.baseUrl.baseUrl
  return axios.create()({
    method: 'GET',
    url: `${baseUrl}/authentication/portal/getAuthentication`,
    params: {
      key: state.user.userInfo.token
    }
  })
}

const debounceRefreshToken = debounce(refreshToken, 1000, {
  leading: true,
  trailing: false
})

// 辅助函数：处理token刷新后的用户信息更新
function handleTokenRefreshResponse(res: any) {
  // res 是完整的 axios response，实际数据在 res.data.data 中
  const data = res.data?.data ?? res.data
  const userInfo = {
    name: data.info.meta.Name,
    uid: data.info.uid,
    token: data.token,
    expired: data.expired
  }
  store.commit('user/setUserInfo', userInfo)
  return userInfo
}

// 控制token刷新状态，防止多个请求同时触发刷新
let isRefreshing = false
// 存储刷新token期间过来的401请求，等待token刷新后重新发送
let requests: any[] = []

// 公共函数：处理token刷新逻辑
const handleTokenRefresh = (error: any) => {
  // token 无效 (没有提供 token、token 是无效的、token 过期)
  if (!state.user.userInfo) {
    ElMessage.warning($t('main_request_tip_login_expired'))
    // 没有登录
    store.commit('user/redirectLogin')
    return Promise.reject(error)
  }
  // 刷新 token
  if (!isRefreshing) {
    isRefreshing = true
    // 尝试刷新 token
    return refreshToken()
      .then((res: any) => {
        handleTokenRefreshResponse(res)
        // 重新发送队列中的请求
        requests.forEach(cb => cb())
        // 重置 requests 数组
        requests = []
        return request(error.config)
      })
      .catch((refreshError: any) => {
        // 刷新token失败，清空队列并重定向到登录页
        requests = []
        ElMessage.warning($t('main_request_tip_login_expired'))
        store.commit('user/setUserInfo', null)
        store.commit('user/redirectLogin')
        return Promise.reject(refreshError)
      })
      .finally(() => {
        isRefreshing = false // 重置刷新状态
      })
  }
  // 正在刷新状态下，把请求挂起放到 requests 数组中
  return new Promise(resolve => {
    requests.push(() => {
      resolve(request(error.config))
    })
  })
}

const popErrorMessage = (message: string) => {
  ElMessage({ message, type: 'error', customClass: 'zIndex-6666' })
}

export function checkTokenExpired() {
  // 根据expired判断token是否过期
  const { userInfo } = state.user
  if (userInfo.expired && userInfo.token) {
    const isExpired = dayjs().isAfter(dayjs(userInfo.expired))

    if (isExpired) {
      return debounceRefreshToken()
        .then((res: any) => {
          handleTokenRefreshResponse(res)
        })
        .catch(() => false)
    }
  }
  return Promise.resolve()
}

request.interceptors.request.use(
  async (config: AxiosRequest) => {
    const { userInfo } = state.user
    if (userInfo) {
      if (!config.notAuth) {
        config.headers.Authorization = `Bearer ${userInfo.token}`
        await checkTokenExpired()
      }
    }
    if (!(config as any).isNotAuth && !JSON.parse(localStorage.getItem('userInfo') as string)) {
      store.commit('user/redirectLogin')
    }

    const language = getLanguage()
    if (language) {
      config.headers['Accept-Language'] = language
      config.headers.lang = language
    }

    const userTimeZone = localStorage.getItem('UserTimeZone')

    if (userTimeZone) {
      config.headers['user-time-zone'] = userTimeZone
    }

    const baseUrl = state.baseUrl.baseUrl
    // const baseUrl = 'https://services-portal-uat.yunexpress.com'
    if (config.url?.startsWith('/File') || config.url?.startsWith('http')) {
      return config
    }

    if (baseUrl) {
      config.url = `${baseUrl}${config.url}`
      // config.url = config.url
    } else if (env) {
      config.url = `http://${
        env.toLowerCase() === 'dev' ? 'gateway' : 'aligateway'
      }.${env.toLowerCase()}.yunexpress.com${config.url}`
    } else {
      store.commit('user/redirectLogin')
      return Promise.reject(new Error($t('main_request_tip_exist_environ')))
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

const fileToJson = (res: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsText(res)
    reader.onload = (result: any) => {
      let msg = ''
      try {
        const resData = JSON.parse(result.target.result)
        if (resData) {
          msg = resData.display || resData.tips || resData.Display || resData.Tips
        }
      } catch (error) {
        console.log(error)
      }
      resolve(msg)
    }
  })
}

request.interceptors.response.use(
  (response: AxiosResponseConfig) => {
    // 1. 处理后端 ResultVo 结构
    const res = response.data as ResultVo<any>

    // 业务成功 (status=1 或者 msg存在且code=200)
    if (res?.status === 1 || (res?.msg && res?.code === 200)) {
      return res // 直接返回完整响应数据
    }

    // 2. 业务失败 (status=0||status不存在||) - 统一抛出错误（包含错误码）
    const error: any = new Error(res.message || res.msg || $t('main_request_tip_system_error'))
    error.code = res.code // 保留业务错误码
    error.isBusinessError = true // 标记为业务错误
    error.response = response // 保留响应对象

    // 处理业务401错误
    if (res.code === 401) {
      console.log('处理业务401错误') // 添加调试日志
      return handleTokenRefresh(error)
    }

    // 直接处理其他业务错误，不依赖第二个回调函数
    if (error.isBusinessError) {
      console.log('直接处理业务错误') // 添加调试日志
      const errorMessage = error.message || $t('main_request_tip_system_error')
      // 只有当配置允许显示消息时才弹出错误
      if (!(response.config as any)?.isNotMessage) {
        popErrorMessage(errorMessage)
      }
      // // 返回一个被拒绝的Promise，包含错误信息
      // return Promise.reject(error)
    }

    // 对于非业务错误，继续抛出错误让第二个回调函数处理
    return Promise.reject(error)
  },
  async error => {
    if (error.config?.silence) return Promise.reject(error)

    // 统一错误处理
    const errFormat = {
      message: '',
      tips: '',
      status: null
    }

    // 处理HTTP错误
    if (error.response) {
      // 请求发出收到响应，但是状态码超出 2xx 范围
      const { status, data } = error.response
      // 优先使用业务错误信息（来自 ResultVo）
      if (error.message) {
        errFormat.message = error.message
      } else if (typeof data === 'string') {
        errFormat.message = data
      } else if (Object.prototype.toString.call(data) === '[object Object]') {
        errFormat.message =
          (data as any).Display ||
          (data as any).display ||
          (data as any).Tips ||
          (data as any).tips ||
          $t('main_request_tip_system_error')
      } else {
        const message: any = await fileToJson(data)
        errFormat.message = message
      }
      errFormat.status = status

      // 根据状态码处理不同错误
      if (status === 401) {
        return handleTokenRefresh(error)
      } else if (status === 403) {
        popErrorMessage($t('main_request_tip_no_legal'))
      } else if (status.toString().startsWith('4')) {
        if (!error.config?.isNotMessage) {
          errFormat.message
            ? popErrorMessage(errFormat.message)
            : popErrorMessage($t('main_request_tip_system_error'))
        }
      } else if (status === 504) {
        popErrorMessage($t('main_request_504_error'))
      } else if (status >= 500) {
        popErrorMessage($t('main_request_tip_server_error'))
      }
    } else if (error.request) {
      popErrorMessage($t('main_request_tip_timeout'))
    }

    return Promise.reject(errFormat)
  }
)

export default request
